import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api/client";
import { useSession } from "@/lib/auth/session";
import { PERMISSIONS } from "@/lib/permissions/catalog";

type PendingItem = {
  kind: "prospect" | "invitation";
  prospectId: number | null;
  invitationId: number | null;
  name: string;
  reason: string;
  reasonLabel: string;
  daysSince: number;
  deepLink: string;
};

/**
 * Campana del topbar de Nexus: "mis pendientes de seguimiento" (prospectos sin
 * contacto + invitaciones sin avanzar), derivados on-the-fly y scopeados al
 * responsable. Refresca cada 2 min y al volver a la pestaña. Solo se muestra a
 * quien tenga SEGUIMIENTO_VIEW.
 */
export function NotificationBell() {
  const hasPermission = useSession((s) => s.hasPermission);
  const enabled = hasPermission(PERMISSIONS.SEGUIMIENTO_VIEW);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const q = useQuery({
    queryKey: ["nexus", "my-pending"],
    queryFn: () =>
      api
        .get("/api/nexus/seguimiento/me/pending")
        .then((r) => r.data as { count: number; items: PendingItem[] }),
    enabled,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });

  if (!enabled) return null;

  const count = q.data?.count ?? 0;
  const items = q.data?.items ?? [];
  const go = (link: string) => {
    setOpen(false);
    navigate(link);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg transition-colors nx-no-tap-highlight"
        aria-label="Pendientes de seguimiento"
        title="Pendientes de seguimiento"
      >
        <Bell size={18} />
        {count > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[15px] h-[15px] px-0.5 rounded-full bg-danger text-white text-[9px] font-bold flex items-center justify-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-full mt-1 w-80 z-40 bg-bg-elevated border border-border rounded-lg shadow-nx-lg overflow-hidden animate-fade-in">
            <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
              <span className="text-sm font-medium text-fg">Mis pendientes de seguimiento</span>
              <span className="nx-badge-neutral">{count}</span>
            </div>
            <div className="max-h-[360px] overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-fg-subtle">
                  {q.isLoading ? "Cargando…" : "Nada por atender. Todo al día 🎉"}
                </div>
              ) : (
                items.map((it, i) => (
                  <button
                    key={`${it.kind}-${it.prospectId ?? it.invitationId}-${i}`}
                    onClick={() => go(it.deepLink)}
                    className="w-full text-left px-3 py-2.5 hover:bg-bg-hover transition-colors border-b border-border-subtle last:border-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-fg truncate">{it.name}</span>
                      <span className="text-2xs text-danger font-semibold whitespace-nowrap">
                        {it.daysSince} días
                      </span>
                    </div>
                    <div className="text-2xs text-fg-subtle">{it.reasonLabel}</div>
                  </button>
                ))
              )}
            </div>
            <button
              onClick={() => go("/seguimiento/funnel?alert=true")}
              className="w-full px-3 py-2 text-xs text-accent hover:bg-bg-hover transition-colors border-t border-border"
            >
              Ver todo en Seguimiento →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
